import React, { createContext, useState, useContext, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import api from '../services/api';

const WishlistContext = createContext();

const GUEST_WISHLIST_KEY = 'guestWishlist';
const LEGACY_GUEST_WISHLIST_KEY = 'guest_wishlist_v1';

function safeParse(json, fallback) {
  try {
    return JSON.parse(json);
  } catch (e) {
    return fallback;
  }
}

function normalizeGuestWishlistItems(rawItems) {
  const arr = Array.isArray(rawItems) ? rawItems : [];
  return arr
    .map((it) => {
      const pid = (it.productId ?? it._id ?? it.id)?.toString?.();
      if (!pid) return null;
      const unitPrice = Number(it.unitPrice ?? it.price ?? 0);
      return {
        productId: pid,
        name: it.name,
        image: it.image,
        unitPrice,
        price: unitPrice,
        stockStatus: it.stockStatus || null,
        stockQuantity: Number(it.stockQuantity ?? 0),
        addedAt: it.addedAt || new Date().toISOString(),
      };
    })
    .filter(Boolean);
}

function normalizeGuestProduct(snapshot) {
  if (!snapshot) return null;
  const qty = Number(snapshot?.stock?.quantity ?? 0);
  const status =
    snapshot?.stock?.status || (qty <= 0 ? 'out_of_stock' : qty < 10 ? 'low_stock' : 'in_stock');

  const rawPrice = Number(snapshot?.price ?? 0);
  const discount = Number(snapshot?.discount ?? 0);
  const unitPrice =
    Number(snapshot?.unitPrice ?? (discount > 0 ? rawPrice * (1 - discount / 100) : rawPrice)) || 0;

  return {
    name: snapshot?.name || '',
    image: snapshot?.image ?? snapshot?.images?.main ?? '',
    unitPrice,
    price: unitPrice,
    stock: { quantity: qty, status },
  };
}

function readGuestWishlistFromStorage() {
  const legacy = safeParse(localStorage.getItem(LEGACY_GUEST_WISHLIST_KEY), null);
  const legacyItems = legacy && Array.isArray(legacy.items) ? legacy.items : null;
  const stored = safeParse(localStorage.getItem(GUEST_WISHLIST_KEY), null);
  const items = normalizeGuestWishlistItems(legacyItems ?? stored ?? []);

  localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(items));
  localStorage.removeItem(LEGACY_GUEST_WISHLIST_KEY);

  return { items, count: items.length };
}

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const [guestWishlist, setGuestWishlist] = useState({ items: [], count: 0 });
  const [actionLock, setActionLock] = useState(false);

  const wishlistQueryKey = useMemo(() => ['wishlist', user?._id], [user?._id]);

  const wishlistQuery = useQuery({
    queryKey: wishlistQueryKey,
    queryFn: async () => (await api.get('wishlist')).data.wishlist,
    enabled: Boolean(isAuthenticated && user?._id),
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      setGuestWishlist(readGuestWishlistFromStorage());
    }
  }, [isAuthenticated, user]);

  const serverWishlist = wishlistQuery.data ?? { items: [], count: 0 };
  const wishlist = isAuthenticated ? serverWishlist : guestWishlist;

  const setWishlistCache = useCallback(
    (next) => {
      queryClient.setQueryData(wishlistQueryKey, next);
    },
    [queryClient, wishlistQueryKey]
  );

  const invalidateWishlist = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['wishlist'] });
  }, [queryClient]);

  useEffect(() => {
    const handler = (event) => {
      const nextWishlist = event?.detail?.wishlist;
      if (nextWishlist) {
        if (isAuthenticated) {
          setWishlistCache(nextWishlist);
        } else {
          setGuestWishlist(nextWishlist);
        }
      }
    };

    window.addEventListener('ecomm:cart-sync', handler);
    return () => window.removeEventListener('ecomm:cart-sync', handler);
  }, [isAuthenticated, setWishlistCache]);

  const loading = (isAuthenticated ? wishlistQuery.isFetching : false) || actionLock;

  const toggleWishlist = useCallback(
    async (productId, productSnapshot = null) => {
      if (actionLock) return;

      setActionLock(true);

      try {
        if (!isAuthenticated) {
          const pid = productId?.toString?.() ?? String(productId);
          const currentItems = normalizeGuestWishlistItems(safeParse(localStorage.getItem(GUEST_WISHLIST_KEY), []));
          const existingIdx = currentItems.findIndex((it) => it.productId === pid);

          if (existingIdx >= 0) {
            const nextItems = [...currentItems];
            nextItems.splice(existingIdx, 1);
            localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(nextItems));
            setGuestWishlist({ items: nextItems, count: nextItems.length });

            window.dispatchEvent(
              new CustomEvent('ecomm:wishlist-sync', {
                detail: { wishlist: { items: nextItems, count: nextItems.length } },
              })
            );

            return { success: true, isWishlisted: false };
          }

          let product = normalizeGuestProduct(productSnapshot);
          if (!product) {
            const stockRes = await api.get(`products/bulk?ids=${productId}`);
            product = normalizeGuestProduct(stockRes.data.products?.[0]);
          }
          if (!product) return { success: false, message: 'Product not found' };

          const item = {
            productId: pid,
            name: product.name,
            image: product.image,
            unitPrice: Number(product.unitPrice ?? product.price ?? 0),
            price: Number(product.unitPrice ?? product.price ?? 0),
            stockStatus: product.stock?.status || null,
            stockQuantity: Number(product.stock?.quantity ?? 0),
            addedAt: new Date().toISOString(),
          };

          const cartRes = await api.delete(`cart/remove/${productId}`).catch(() => null);
          const nextItems = [item, ...currentItems];
          localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(nextItems));
          setGuestWishlist({ items: nextItems, count: nextItems.length });

          window.dispatchEvent(
            new CustomEvent('ecomm:wishlist-sync', {
              detail: {
                wishlist: { items: nextItems, count: nextItems.length },
                ...(cartRes?.data?.cart ? { cart: cartRes.data.cart } : {}),
              },
            })
          );

          return { success: true, isWishlisted: true };
        }

        const response = await api.post('wishlist/toggle', { productId });
        setWishlistCache(response.data.wishlist);

        window.dispatchEvent(
          new CustomEvent('ecomm:wishlist-sync', {
            detail: { cart: response.data.cart, wishlist: response.data.wishlist },
          })
        );

        return { success: true, isWishlisted: response.data.isWishlisted };
      } catch (error) {
        invalidateWishlist();
        return {
          success: false,
          message: error.response?.data?.message || 'Failed to update wishlist',
        };
      } finally {
        setTimeout(() => setActionLock(false), 300);
      }
    },
    [isAuthenticated, actionLock, setWishlistCache, invalidateWishlist]
  );

  const isWishlisted = useCallback(
    (productId) => {
      return wishlist.items.some(
        (item) => item.productId?.toString() === productId?.toString() || item.productId === productId
      );
    },
    [wishlist.items]
  );

  const moveWishlistToCart = useCallback(
    async (productId, quantity = 1) => {
      if (actionLock) return;

      setActionLock(true);

      try {
        if (!isAuthenticated) {
          const pid = productId?.toString?.() ?? String(productId);
          const currentItems = normalizeGuestWishlistItems(safeParse(localStorage.getItem(GUEST_WISHLIST_KEY), []));
          const existingIdx = currentItems.findIndex((it) => it.productId === pid);
          if (existingIdx < 0) return { success: false, message: 'Item not in wishlist' };

          const cartRes = await api.post('cart/add', { productId, quantity });

          const nextItems = [...currentItems];
          nextItems.splice(existingIdx, 1);
          localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(nextItems));
          setGuestWishlist({ items: nextItems, count: nextItems.length });

          window.dispatchEvent(
            new CustomEvent('ecomm:wishlist-sync', {
              detail: { cart: cartRes.data.cart, wishlist: { items: nextItems, count: nextItems.length } },
            })
          );

          return { success: true };
        }

        const response = await api.post('wishlist/move-to-cart', { productId, quantity });
        setWishlistCache(response.data.wishlist);

        window.dispatchEvent(
          new CustomEvent('ecomm:wishlist-sync', {
            detail: { cart: response.data.cart, wishlist: response.data.wishlist },
          })
        );

        return { success: true };
      } catch (error) {
        return { success: false, message: error.response?.data?.message || 'Failed to move item' };
      } finally {
        setTimeout(() => setActionLock(false), 300);
      }
    },
    [actionLock, isAuthenticated, setWishlistCache]
  );

  const fetchWishlist = useCallback(() => {
    if (isAuthenticated) {
      return invalidateWishlist();
    }
    setGuestWishlist(readGuestWishlistFromStorage());
    return Promise.resolve();
  }, [isAuthenticated, invalidateWishlist]);

  const value = {
    wishlist,
    loading,
    toggleWishlist,
    isWishlisted,
    moveWishlistToCart,
    fetchWishlist,
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};
